import { memo, useCallback, useState, useEffect } from 'react'
import { CS, CS_m } from 'back-end-api'
import { useTranslation } from 'services/Translation'
import { usePatientsState, usePatientsDispatch } from 'services/Patients'
// eslint-disable-next-line import/named
import { getDiseaseIdentifiers } from 'services/Patients/queries'
import InputText from 'components/common/inputs/Text'
import Separator from 'components/common/Separator'
import Typography from 'components/common/Typography'
import UnderlineBtn from 'components/common/buttons/Underline'
import { usePatientRouteParams } from 'components/patient/Parameters'
import { useStatusTranslation } from 'services/Registrations'
import { hp } from 'styles/Sizes'

import Actions from './Actions'
import { formatDiseases } from '../Tools'
import Selector from './Selector'
import PendingRequest from './PendingRequest'
import SelectedDiseases from './SelectedDiseases'
import { Icpc } from '../Types'
import { useReadOnly } from 'components/patient/ReadOnlyContext'
import useIsAssistant from 'hooks/useIsAssistant'

const texts = (getText: (text: string, key: string) => string) => (key: string) =>
  ({
    title: getText('Actions', 'Patient_Details_Actions'),
    titleEvaluation: getText('Your evaluation', 'Patient_Details_Actions'),
    titleIcpc: getText(
      'Please choose an ICPC code to finish the consultation',
      'Patient_Details_Actions'
    ),
    selectedIcpcs: getText('Chosen code(s)', 'Patient_Details_Actions'),
    content: getText('Please enter your evaluation here.', 'Patient_Details_Actions'),
    makeEdit: getText('Make an edit', 'Patient_Details_Actions'),
    done: getText('Done', 'Patient_Details_Actions'),
    avStatus: getText('AV', 'Patient_Details_Status'),
  }[key])

const AV_STATUS_COLORS: Record<string, string> = {
  pending: '#8B5CF6',
  accepted: '#7C3AED',
  ready: '#06B6D4',
  inProgress: '#2563EB',
  done: '#10B981',
  declined: '#EF4444',
  cancelled: '#EF4444',
  timeout: '#DC2626',
  missed: '#B91C1C',
}

const AV_STATUS_LABEL: Record<string, string> = {
  [CS_m.ModelsAppointmentStatus.pending]: 'AppointmentPending',
  [CS_m.ModelsAppointmentStatus.accepted]: 'AppointmentAccepted',
  [CS_m.ModelsAppointmentStatus.ready]: 'AppointmentReady',
  [CS_m.ModelsAppointmentStatus.inProgress]: 'AppointmentInProgress',
  [CS_m.ModelsAppointmentStatus.done]: 'AppointmentDone',
  [CS_m.ModelsAppointmentStatus.declined]: 'AppointmentDeclined',
  [CS_m.ModelsAppointmentStatus.cancelled]: 'AppointmentCancelled',
  [CS_m.ModelsAppointmentStatus.timeout]: 'AppointmentTimeout',
  [CS_m.ModelsAppointmentStatus.missed]: 'AppointmentMissed',
}

interface DiagnosisProps {
  codes: Icpc[]
  setCodes: (codes: Icpc[]) => void
  token: string
  mandatoryActionTaken: boolean
}

const Diagnosis = ({ codes, setCodes, token, mandatoryActionTaken }: DiagnosisProps) => {
  const { summary, appointment } = usePatientRouteParams()
  const readOnly = useReadOnly()
  const isAssistant = useIsAssistant()
  const tStatus = useStatusTranslation()

  const { t, lang } = useTranslation(texts)
  const { diseases: rawDiseases, diseasesLang } = usePatientsState()
  const patientsDispatch = usePatientsDispatch()
  const [evaluationNotes, setEvaluationNotes] = useState(summary?.doctor_evaluation)
  const [editEvaluationNotes, setEditEvaluationNotes] = useState(false)
  const [editLoading, setEditLoading] = useState(false)

  // Re-sync local evaluation notes when switching patients/summaries.
  // useState only initializes once, so without this the previous patient's
  // evaluation would persist until a full page refresh remounts the component.
  useEffect(() => {
    setEvaluationNotes(summary?.doctor_evaluation)
    setEditEvaluationNotes(false)
  }, [summary?.id, summary?.doctor_evaluation])
  const formattedDiseases = formatDiseases(rawDiseases, lang)

  const handleDelete = useCallback(
    (idx: number) => {
      const newCodes = [...codes]
      newCodes.splice(idx, 1)
      setCodes(newCodes)
    },
    [codes, setCodes]
  )

  useEffect(() => {
    // Refetch when the list has never been loaded, or when the dashboard language
    // changed since it was last loaded (the ICPC names are language-specific).
    if (!rawDiseases?.length || diseasesLang !== lang) {
      ;(async () => {
        await getDiseaseIdentifiers({ token }, { system: 'icpc', lang }, patientsDispatch, true)
      })()
    }
  }, [rawDiseases, diseasesLang, patientsDispatch, token, lang])

  const handleEvaluationNotes = useCallback(({ target: { value } }: any) => {
    setEvaluationNotes(value)
  }, [])

  const confirmEdit = useCallback(async () => {
    if (!summary?.id) return
    setEditLoading(true)
    await CS.updateSummary({
      id: summary.id,
      doctor_evaluation: evaluationNotes,
    })
    setEditLoading(false)
    setEditEvaluationNotes(false)
  }, [evaluationNotes, summary?.id])

  const onEdit = useCallback(() => {
    setEditEvaluationNotes(true)
  }, [])

  if (
    (appointment?.status === CS_m.ModelsAppointmentStatus.pending ||
      appointment?.status === CS_m.ModelsAppointmentStatus.accepted) &&
    !appointment?.is_immediate
  ) {
    return <PendingRequest />
  }

  return (
    <div className="h-full">
      <div className="font-csc65 flex flex-col text-lg py-3 mb-3 mt-8">
        {mandatoryActionTaken && (
          <>
            <div className="font-csc65 flex justify-start items-center">
              {t('titleEvaluation')}
              {!CS.isSummaryStatusFinal(summary) && !readOnly && !isAssistant && (
                <UnderlineBtn
                  className="ml-12 pl-12 border-l border-grey-100"
                  bold
                  disabled={editLoading}
                  withNext
                  label={editEvaluationNotes ? t('done') : t('makeEdit')}
                  onClick={editEvaluationNotes ? confirmEdit : onEdit}
                  innerRef={undefined}
                  previousIcon={undefined}
                  nextIcon={undefined}
                  style={undefined}
                />
              )}
            </div>
            {(!isAssistant || evaluationNotes) && (
            <InputText
              className="mt-3 font-csc45"
              field={{
                onChange: handleEvaluationNotes,
                value: evaluationNotes,
              }}
              placeholder={t('content')}
              noBorder
              noPadding
              multiline
              readOnly={readOnly || isAssistant || !editEvaluationNotes || CS.isSummaryStatusFinal(summary)}
              inputStyle={{ lineHeight: '25px', fontSize: '15px', padding: '8px 0', borderBottom: '1.5px solid #E5E7EB', background: 'transparent' }}
              containerStyle={{ flex: 1, maxWidth: '50%' }}
              style={undefined}
              label={undefined}
              inputRef={undefined}
              placeholderStyle={undefined}
            />
            )}
            {/* Ill / Not-ill (incapacitated) feature disabled */}
            {/* <IncapacitatedSelector /> */}
          </>
        )}
      </div>
      <hr className="border-grey-100 my-4" />
      <div className="font-csc65 flex items-center text-lg py-3 mb-3 mt-12">
        {t('title')}
        {appointment?.status && (
          <span
            style={{
              fontSize: '0.8125rem',
              fontFamily: 'coresanscr65, sans-serif',
              marginLeft: 16,
              padding: '2px 10px',
              borderRadius: 4,
              color: AV_STATUS_COLORS[appointment.status] || '#8B5CF6',
              background: `${AV_STATUS_COLORS[appointment.status] || '#8B5CF6'}14`,
              border: `1px solid ${AV_STATUS_COLORS[appointment.status] || '#8B5CF6'}33`,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              lineHeight: 1,
              whiteSpace: 'nowrap' as const,
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="23 7 16 12 23 17 23 7" />
              <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
            </svg>
            {t('avStatus')}: {tStatus(AV_STATUS_LABEL[appointment.status] ?? '')}
          </span>
        )}
      </div>
      <Actions />
      <hr className="border-grey-100 my-6" />
      {mandatoryActionTaken && (
        <>
          <Typography
            type="title"
            text={!CS.isSummaryStatusFinal(summary) ? t('titleIcpc') : t('selectedIcpcs')}
            className="font-csc65 mt-12"
            bold
            size={20 * 0.8}
          />
          <Separator size={hp(3.7)} />
          <div className="relative w-full flex-wrap flex justify-end">
            <Selector
              icpcs={formattedDiseases}
              codes={codes}
              setCodes={setCodes}
              disabled={isAssistant || readOnly || CS.isSummaryStatusFinal(summary)}
            />
            <SelectedDiseases
              codes={codes}
              onDelete={handleDelete}
              disabled={isAssistant || readOnly || CS.isSummaryStatusFinal(summary)}
            />
          </div>
        </>
      )}
    </div>
  )
}

Diagnosis.whyDidYouRender = true

export default memo(Diagnosis)
